/**
 * The chips.  Every group is a set, and an empty set means "no opinion"
 * — so a reviewer narrows by adding rather than by remembering to
 * clear.
 *
 * Each chip carries the count it WOULD show, taken from the facets the
 * server computed over the current scope before any chip was applied.
 * That is what makes a zero informative: "there are no capstones on
 * this planet" is an answer, and a chip list built from the rows on
 * screen could never give it — the value would simply not be there to
 * click.
 *
 * One arithmetic warning worth keeping in mind while reading them: the
 * level / type / assessment / mode / demonstrates counts are ANY-OF over
 * each unit's INSTRUMENTS, so they add up to more than the rows on the shelf.
 * `vessel`, `provenance` and `review` are the unit's own single-valued
 * facts and do sum to the row count.
 */

import {
  CHIP_GROUPS,
  EMPTY_BUCKET,
  EMPTY_QUERY,
  hasChips,
  toggle,
  type ChipGroup,
  type Chips,
} from "./filters";
import type { Facets, Vocabulary } from "./types";
import { Button, Chip, Label } from "./ui";

const HEADINGS: Record<ChipGroup, string> = {
  level: "level",
  type: "type",
  vessel: "vessel",
  review: "review",
  assessment: "assessment",
  mode: "icap mode",
  demonstrates: "demonstrates",
  provenance: "provenance",
};

/**
 * What a unit's level and type actually mean, said once where a reviewer
 * meets them — the two grains on this page are easy to conflate and the
 * conflation makes the counts look wrong.
 */
const HINTS: Partial<Record<ChipGroup, string>> = {
  level: "any-of: a unit matches when any instrument it embeds is at this level",
  type: "any-of: the priceable instrument's shape — stake, rep, scenario, capstone",
  vessel: "vesselType: the shape the unit itself is offered out of",
};

/** The groups a reviewer reaches for first, open by default. */
const PRIMARY: ChipGroup[] = ["level", "type", "vessel", "review"];

export function FilterBar({
  chips,
  facets,
  vocabulary,
  onChange,
  showAll,
  onShowAll,
}: {
  chips: Chips;
  facets: Facets | null;
  vocabulary: Vocabulary | null;
  onChange: (chips: Chips) => void;
  showAll: boolean;
  onShowAll: (open: boolean) => void;
}) {
  const groups = showAll ? [...CHIP_GROUPS] : PRIMARY;

  const options = (group: ChipGroup): string[] => {
    // The CONTRACT's vocabulary, not the data's: an authored vessel type
    // nobody has published yet is a real answer ("none of these"), and a
    // chip list drawn from the rows could not say it.
    const declared = vocabulary ? vocabulary[group].map(String) : [];
    const seen = facets ? Object.keys(facets[group]) : [];
    // The "nothing declared" bucket is counted but not filterable — the
    // wire's vocabularies are closed and drop it — so it is never drawn.
    const empty = EMPTY_BUCKET[group];
    return [
      ...declared,
      ...seen.filter((value) => value !== empty && !declared.includes(value)),
    ];
  };

  return (
    <div data-testid="library-filters" style={{ display: "grid", gap: 10 }}>
      {groups.map((group) => (
        <div key={group} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Label style={{ width: 92, flexShrink: 0 }} title={HINTS[group]}>
            {HEADINGS[group]}
          </Label>
          {options(group).map((value) => (
            <Chip
              key={value}
              testId={`filter-${group}-${value}`}
              active={chips[group].includes(value)}
              count={facets ? (facets[group][value] ?? 0) : undefined}
              onClick={() => onChange({ ...chips, [group]: toggle(chips[group], value) })}
            >
              {group === "level" ? `L${value}` : value}
            </Chip>
          ))}
        </div>
      ))}

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Label style={{ width: 92, flexShrink: 0 }}>condition</Label>
        <Chip
          testId="filter-hide-drained"
          active={chips.hideDrained}
          count={facets ? facets.tombstoned : undefined}
          onClick={() => onChange({ ...chips, hideDrained: !chips.hideDrained })}
        >
          hide drained
        </Chip>
        <Chip
          testId="filter-dangling"
          active={chips.danglingOnly}
          count={facets ? facets.dangling : undefined}
          onClick={() => onChange({ ...chips, danglingOnly: !chips.danglingOnly })}
        >
          dangling claims only
        </Chip>
        {/* NOT the same as `review: ["pending"]`, and the difference is
            the point: a flagged unit has been read but is not done with,
            so draining the queue has to keep it visible. */}
        <Chip
          testId="filter-exclude-reviewed"
          active={chips.excludeReviewed}
          count={facets ? (facets.review.reviewed ?? 0) : undefined}
          onClick={() => onChange({ ...chips, excludeReviewed: !chips.excludeReviewed })}
        >
          hide reviewed
        </Chip>

        <span style={{ flex: 1 }} />

        <Button testId="filter-more" onClick={() => onShowAll(!showAll)}>
          {showAll ? "fewer filters" : "more filters"}
        </Button>
        {hasChips(chips) ? (
          // Cleared back to the ONE declared empty value rather than to a
          // literal written out here: a chip group added to `Chips` and
          // forgotten in this object is a filter that silently cannot be
          // cleared.
          <Button testId="filter-clear" onClick={() => onChange({ ...EMPTY_QUERY.chips })}>
            clear
          </Button>
        ) : null}
      </div>
    </div>
  );
}
